
import { useState } from "react";
import { logActivity } from "@/utils/userUtils";
import { useAuthentication } from "./useAuthentication";
import { useNetworkStatus } from "./useNetworkConnection";
import { useOrdersManagement, Order } from "./useOrdersManagement";

type ViewType = "dashboard" | "products" | "prices" | "backend" | "edit-order" | "employee-clockin" | "employee-management" | "employee-time-calendar" | "payroll-processing" | "employee-profile" | "credit-reports" | "cost-monitor" | "login-attempts" | "store-station" | "scan";

export const useIndexState = () => {
  const [currentView, setCurrentView] = useState<ViewType>("dashboard");
  const [dashboardMode, setDashboardMode] = useState<"staff" | "admin">("staff");
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  // Use separated hooks with proper authentication timing
  const {
    hasLocationAccess,
    setHasLocationAccess,
    userName,
    setUserName,
    shouldFetchData,
    isAuthReady
  } = useAuthentication();

  const { isOnline } = useNetworkStatus();
  const networkStatus: "verified" | "failed" | "checking" = isOnline ? "verified" : "failed";
  const checkNetworkStatus = () => {};

  const {
    orders,
    ordersLoading,
    ordersError,
    handleOrderCheckIn,
    testSupabaseConnection
  } = useOrdersManagement(shouldFetchData, isAuthReady);

  const handleViewProducts = (order: Order) => {
    console.log('🎯 handleViewProducts clicked:', { 
      orderId: order.id, 
      supplier: order.supplierName,
      productCount: order.productCount,
      currentRoute: window.location.pathname
    });
    logActivity('Viewed order products', { orderId: order.id, supplier: order.supplierName });
    
    // Navigate to staff-only products view from staff dashboard
    if (window.location.pathname === '/staff-dashboard') {
      console.log('🚀 Navigating to staff products view:', `/orders/${order.id}/products`);
      window.location.href = `/orders/${order.id}/products`;
    } else {
      // Fallback for other contexts
      setSelectedOrder(order);
      setCurrentView("products");
    }
  };

  const handleEditOrder = (order: Order) => {
    logActivity('Started editing order', { orderId: order.id, supplier: order.supplierName });
    setSelectedOrder(order);
    setCurrentView("edit-order");
  };

  const handleDeleteOrder = () => {
    setSelectedOrder(null);
    setCurrentView("dashboard");
  };

  return {
    currentView,
    setCurrentView,
    dashboardMode,
    setDashboardMode,
    selectedOrder,
    setSelectedOrder,
    hasLocationAccess,
    setHasLocationAccess,
    networkStatus,
    userName,
    setUserName,
    orders,
    ordersLoading,
    ordersError,
    handleOrderCheckIn,
    handleViewProducts,
    handleEditOrder,
    handleDeleteOrder,
    checkNetworkStatus,
    testSupabaseConnection
  };
};
